"use client";

import { c as _c } from "react/compiler-runtime";
import { MapPin } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useAuthLeftContent } from "~/app/auth/_context/AuthLeftContentContext";
import type { AuthStep, LeftContent } from "~/app/auth/_lib/auth-content";
import { getHostAddressContent, getLeftContent } from "~/app/auth/_lib/auth-content";
import { consumeAuthFlowNotice } from "~/app/auth/_lib/auth-flow-notice";
import { AuthPanel } from "~/components/auth/AuthPanel";
import { OtpVerification } from "~/components/auth/OtpVerification";
import type { ProfileData } from "~/lib/api/profile";
import { extractProfile, getMissingFields } from "~/lib/api/profile";
import type { User } from "~/lib/api/schema";
import { authClient } from "~/lib/auth/client";
import type { AuthUser } from "~/lib/auth/types";
import { readHostAddress } from "~/lib/onboarding/host-address";
import { api } from "~/trpc/react";
import { IdentifyStep } from "./IdentifyStep";
import { NameStep } from "./NameStep";
import { SecondaryContactStep } from "./SecondaryContactStep";
function extractMetaName(meta: AuthUser["metadata"]): {
  firstName: string;
  lastName: string;
} {
  let first = meta.givenName.trim();
  let last = meta.familyName.trim();
  if (!first && !last) {
    const full = meta.fullName.trim();
    const i = full.indexOf(" ");
    first = i === -1 ? full : full.slice(0, i);
    last = i === -1 ? "" : full.slice(i + 1);
  }
  return {
    firstName: first,
    lastName: last
  };
}
function sanitizeNext(raw: string | null): string {
  if (!raw) return "/";
  // Must be a relative path — reject external URLs, protocol-relative URLs, and /auth to avoid loops
  if (!raw.startsWith("/") || raw.startsWith("//") || raw === "/auth" || raw.startsWith("/auth?") || raw.startsWith("/auth/")) {
    return "/";
  }
  return raw;
}
type AuthChannel = "phone" | "email" | "oauth";
export function UnifiedAuthPage({
  initialLeftContent = null,
  personalizeHostAddress = false
}: {
  initialLeftContent?: Partial<LeftContent> | null;
  /**
   * On the way to host onboarding, name the address picked on the host landing
   * page (if any) in the left panel and, on mobile where that panel is hidden,
   * above the form.
   */
  personalizeHostAddress?: boolean;
}) {
  const searchParams = useSearchParams();
  const {
    setLeftContent
  } = useAuthLeftContent();
  const next = sanitizeNext(searchParams.get("next"));
  const utils = api.useUtils();
  const [step, setStep] = useState<AuthStep>("identify");
  const [channel, setChannel] = useState<AuthChannel>("phone");
  const [identifier, setIdentifier] = useState("");
  const [rawInput, setRawInput] = useState("");
  const [profile, setProfile] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    emailVerified: false,
    phoneVerified: false,
    isHost: false
  });
  const [isLoading, setIsLoading] = useState(() => authClient.hasPendingSignup());
  const [secondaryCollect, setSecondaryCollect] = useState<"email" | "phone">("phone");
  // Inline note shown on the identify step after a failed completion (e.g.
  // promote-to-cookie-session failed), so a bounce back to sign-in is explained.
  const [notice, setNotice] = useState<string | null>(null);
  /** Determine the next incomplete onboarding step, or null if done */
  const resolveStep = useCallback((user: User, firstSignup: boolean): AuthStep | null => {
    const missing = getMissingFields(user);
    if (missing.includes("firstName") || missing.includes("lastName")) {
      return "name";
    }

    // Email is required AND must be verified — collect when missing, or re-run the
    // OTP path when an email exists but is not yet verified.
    if (missing.includes("email") || !user.email_verified) {
      setSecondaryCollect("email");
      return "second-auth";
    }
    // Phone stays optional — shown during first signup only, skippable
    if (firstSignup && missing.includes("phone")) {
      setSecondaryCollect("phone");
      return "second-auth";
    }
    return null;
  }, []);

  /**
   * Profile is complete — promote the in-memory signup session into the
   * persistent cookie session, then leave the auth flow. This is the single
   * point where an ephemeral signup becomes a normal, reload-surviving session.
   */
  const finishAuth = useCallback(async () => {
    try {
      await authClient.promoteSignupSession();
    } catch {
      // Couldn't establish the cookie session. The profile is already complete
      // server-side, so the cleanest recovery is a fresh sign-in (fast path) —
      // clear the half-state and return to identify with an explanatory note,
      // rather than navigating into a logged-out page or stranding the user on
      // a verify screen with a consumed code.
      await authClient.signOut();
      setNotice("We couldn't finish signing you in. Please sign in again.");
      setStep("identify");
      return;
    }
    // Hard navigation (not router.replace): a soft transition to a protected
    // `next` can be bounced back to /auth by the proxy (e.g. the session is
    // rejected on arrival) while Next reuses this same /auth component instance
    // — leaving the OTP step mounted with its filled input and spinner (the
    // "stuck auth screen"). A full document load guarantees /auth remounts
    // fresh on the identify step if it's ever shown again.
    window.location.replace(next);
  }, [next]);

  /**
   * Fetch the latest user from NestJS via tRPC, resolve the next step,
   * and either finish (profile complete) or advance to that step.
   */
  const advanceStep = useCallback(async (firstSignup_0 = false) => {
    // Check the session first (still needed to know if authenticated)
    const authUser = await authClient.getUser();
    if (!authUser) {
      setStep("identify");
      return;
    }

    // Invalidate cache and re-fetch fresh data from NestJS via tRPC
    await utils.user.getUser.invalidate();
    const user_0 = await utils.user.getUser.fetch();
    setProfile(extractProfile(user_0));
    const nextStep = resolveStep(user_0, firstSignup_0);
    if (nextStep === null) {
      await finishAuth();
      return;
    }
    if (nextStep === "name") {
      const {
        firstName,
        lastName
      } = extractMetaName(authUser.metadata);
      if (firstName || lastName) {
        setProfile(prev => ({
          ...prev,
          firstName,
          lastName
        }));
      }
    }
    setStep(nextStep);
  }, [resolveStep, utils, finishAuth]);

  // Pick up a transient notice handed over from the callback (e.g. a failed
  // OAuth/magic-link exchange). consume() clears it, and the `if` guard keeps
  // the message across React's Strict Mode double-invoke (2nd run reads null).
  useEffect(() => {
    const pending = consumeAuthFlowNotice();
    if (pending) setNotice(pending);
  }, []);

  // sessionStorage is client-only, so the address is read after mount.
  const [hostStreet, setHostStreet] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (personalizeHostAddress) {
      setHostStreet(readHostAddress()?.streetAddress ?? null);
    }
  }, [personalizeHostAddress]);

  // Sync left panel content with the current auth step before paint to avoid CLS
  useLayoutEffect(() => {
    const stepContent = getLeftContent(step);
    const override = hostStreet ? {
      ...initialLeftContent,
      ...getHostAddressContent(hostStreet)
    } : initialLeftContent;
    const content = override && (step === "identify" || step === "verify-primary") ? {
      ...stepContent,
      ...override,
      hideHomeLink: stepContent.hideHomeLink
    } : stepContent;
    setLeftContent(content);
  }, [step, initialLeftContent, hostStreet, setLeftContent]);

  // Resolve any existing session: fetch the user, then either finish (profile
  // complete → redirect to `next`) or land on the correct step. Covers an OAuth
  // return, a resume after close, and a bfcache Back onto this page after the
  // user signed in and navigated forward.
  const resolveSession = useCallback(async () => {
    try {
      const authUser_0 = await authClient.getUser();
      if (!authUser_0) {
        setIsLoading(false);
        return;
      }

      // A session exists — a redirect (finishAuth) or step advance is coming.
      // Show the skeleton now so we don't flash the identify form underneath it.
      setIsLoading(true);
      const hasOAuth = authUser_0.providers.includes("google") || authUser_0.providers.includes("apple");
      if (hasOAuth) setChannel("oauth");else if (authUser_0.phone) setChannel("phone");else setChannel("email");

      // Fetch app user from NestJS via tRPC. A promoted (cookie) session implies
      // a COMPLETE profile — the app only writes a cookie session at the end of
      // onboarding — so if this fetch fails transiently, don't strand a
      // logged-in user on the sign-in form: leave for `next`. Only a mid-signup
      // ephemeral session actually needs the profile to resolve its step, so
      // rethrow to the outer catch there.
      let user_1: User;
      try {
        user_1 = await utils.user.getUser.fetch();
      } catch (err) {
        if (!authClient.hasPendingSignup()) {
          await finishAuth();
          return;
        }
        throw err;
      }
      setProfile(extractProfile(user_1));
      const nextStep_0 = resolveStep(user_1, false);
      if (nextStep_0 === null) {
        await finishAuth();
        return;
      }
      if (nextStep_0 === "name") {
        const {
          firstName: firstName_0,
          lastName: lastName_0
        } = extractMetaName(authUser_0.metadata);
        if (firstName_0 || lastName_0) {
          setProfile(prev_0 => ({
            ...prev_0,
            firstName: firstName_0,
            lastName: lastName_0
          }));
        }
      }
      setStep(nextStep_0);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [resolveStep, utils, finishAuth]);

  // On mount: check for existing session (OAuth return, resume after close).
  useEffect(() => {
    void resolveSession();
  }, [resolveSession]);
  const handleVerifyPrimary = useCallback(async (code: string) => {
    await authClient.verifyPrimaryOtp(channel === "phone" ? "phone" : "email", identifier, code);
    await advanceStep();
  }, [channel, identifier, advanceStep]);
  const handleResendPrimary = useCallback(async () => {
    await authClient.sendOtp(channel === "phone" ? "phone" : "email", identifier);
  }, [channel, identifier]);
  const handlePhoneOtpSent = useCallback((phone: string, raw: string) => {
    setIdentifier(phone);
    setRawInput(raw);
    setChannel("phone");
    setStep("verify-primary");
  }, []);
  const handleEmailOtpSent = useCallback((email: string) => {
    setIdentifier(email);
    setRawInput(email);
    setChannel("email");
    setStep("verify-primary");
  }, []);
  const handleNameComplete = useCallback(async () => {
    await advanceStep(true);
  }, [advanceStep]);

  // Return the promise so the caller (OTP step) can keep its loading state
  // until promote + navigation actually begin — otherwise the spinner stops
  // while finishAuth is still running and the user appears stuck on the page.
  const handleSecondaryComplete = useCallback(() => finishAuth(), [finishAuth]);
  const handleSecondarySkip = useCallback(() => finishAuth(), [finishAuth]);
  const handleSignOut = useCallback(() => {
    void authClient.signOut().then(() => {
      setStep("identify");
    });
  }, []);
  if (isLoading) {
    return <AuthPanel title="What's your phone number or email?" subtitle="Sign in or create an account">
        <div className="mb-3 h-11 animate-pulse rounded-full bg-[var(--ink-10)] sm:mb-4" />
        <div className="mb-4 h-11 animate-pulse rounded-full bg-[var(--ink-10)]" />
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="mb-4 h-11 animate-pulse rounded-full bg-[var(--ink-10)]" />
        <div className="mx-auto h-3 w-56 animate-pulse rounded bg-[var(--ink-10)]" />
      </AuthPanel>;
  }
  if (step === "verify-primary") {
    return <OtpVerification identifier={identifier} channel={channel as "phone" | "email"} onVerify={handleVerifyPrimary} onResend={handleResendPrimary} onBack={() => setStep("identify")} />;
  }
  if (step === "name") {
    return <NameStep initialFirstName={profile.firstName} initialLastName={profile.lastName} onComplete={handleNameComplete} onBack={handleSignOut} />;
  }
  if (step === "second-auth") {
    return <SecondaryContactStep collect={secondaryCollect} required={secondaryCollect === "email"} initialValue={secondaryCollect === "email" ? profile.email : ""} onComplete={handleSecondaryComplete} onSkip={handleSecondarySkip} />;
  }
  return <>
      {hostStreet ? <HostAddressStrip streetAddress={hostStreet} /> : null}
      <IdentifyStep initialInput={rawInput} notice={notice} next={next} onPhoneOtpSent={handlePhoneOtpSent} onEmailOtpSent={handleEmailOtpSent} onOAuthStarted={() => {}} />
    </>;
}

/** Mobile stand-in for the left panel, which is hidden below `md`. */
function HostAddressStrip(t0) {
  const $ = _c(6);
  const {
    streetAddress
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MapPin className="size-[18px] shrink-0 text-(--blue)" aria-hidden={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== streetAddress) {
    t2 = <p className="truncate text-sm font-bold text-(--ink)">{streetAddress}</p>;
    $[1] = streetAddress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-xs text-(--ink-60)">Ready to list. Sign in to finish.</p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <div className="mb-6 flex items-center gap-3 rounded-(--r-lg) bg-(--blue-xpale) px-4 py-3 font-ui md:hidden">{t1}<div className="min-w-0">{t2}{t3}</div></div>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}